/**
 * Liste affichée du roadbook : filtres, ordre, zones manquantes et
 * regroupement visuel — EF-RBK-003, EF-RBK-007, EF-RBK-012, EF-RBK-013.
 *
 * Le regroupement de EF-RBK-012 est une **vue calculée à l'affichage**, jamais
 * une structure persistée : il ne modifie ni le tri, ni les calculs, ni les
 * fichiers exportés.
 */

import type { CategoryKey } from '../core/categories';
import type { Poi, PoiId, ProgressionInterval, Stop } from '../core/model';
import type { DisplayParameters } from '../core/parameters';
import { comparePoiLines, LINE_TYPE_RANK, orderedPoiLines, type PoiLineRef } from './order';

export type DisplayRowKind = 'stop' | 'missingRange' | 'group';

export interface StopRow {
  readonly kind: 'stop';
  readonly progressionMeters: number;
  readonly stop: Stop;
}

export interface MissingRangeRow {
  readonly kind: 'missingRange';
  readonly progressionMeters: number;
  readonly range: ProgressionInterval;
}

export interface GroupRow {
  readonly kind: 'group';
  readonly progressionMeters: number;
  /** Ancre du groupe : elle porte les valeurs affichées sur la ligne. */
  readonly anchor: PoiLineRef;
  /** Membres du groupe, dans l'ordre de la liste. Au moins un. */
  readonly members: readonly PoiLineRef[];
}

export type DisplayRow = StopRow | MissingRangeRow | GroupRow;

/**
 * Un POI est affiché dès qu'au moins une de ses catégories est visible. Il
 * n'est masqué que lorsque toutes le sont.
 */
export function isPoiVisible(poi: Poi, display: DisplayParameters): boolean {
  if (display.excludedOnly) {
    if (!poi.userState.excluded) return false;
  } else if (poi.userState.excluded) {
    return false;
  }
  if (display.pinnedOnly && !poi.userState.pinned) return false;

  const hidden = new Set<CategoryKey>(display.hiddenCategories);
  return poi.categories.some((key) => !hidden.has(key));
}

interface OrderedLine {
  readonly rank: number;
  readonly progressionMeters: number;
  readonly poi: PoiLineRef | null;
  readonly stop: Stop | null;
  readonly range: ProgressionInterval | null;
}

export interface BuildDisplayInput {
  readonly pois: ReadonlyMap<PoiId, Poi>;
  readonly stops: readonly Stop[];
  readonly missingRanges: readonly ProgressionInterval[];
  readonly display: DisplayParameters;
  readonly routeLengthMeters: number;
}

/**
 * Construit la liste affichée : lignes d'étape, zones manquantes et groupes de
 * proximité, triés selon l'ordre total de EF-CAL-006.
 */
export function buildDisplayRows(input: BuildDisplayInput): readonly DisplayRow[] {
  const visiblePois = orderedPoiLines(input.pois, (poi) => isPoiVisible(poi, input.display));

  const lines: OrderedLine[] = [];

  for (const stop of input.stops) {
    lines.push({
      rank: LINE_TYPE_RANK[stop.type],
      progressionMeters: stop.progressionMeters,
      poi: null,
      stop,
      range: null,
    });
  }
  for (const range of input.missingRanges) {
    lines.push({
      rank: LINE_TYPE_RANK.missingRange,
      progressionMeters: range.fromProgressionMeters,
      poi: null,
      stop: null,
      range,
    });
  }
  for (const ref of visiblePois) {
    lines.push({
      rank: LINE_TYPE_RANK.poi,
      progressionMeters: ref.progressionMeters,
      poi: ref,
      stop: null,
      range: null,
    });
  }

  lines.sort((a, b) => {
    if (a.progressionMeters !== b.progressionMeters) return a.progressionMeters - b.progressionMeters;
    if (a.rank !== b.rank) return a.rank - b.rank;
    if (a.poi !== null && b.poi !== null) return comparePoiLines(a.poi, b.poi, input.pois);
    return 0;
  });

  return groupAdjacentPois(lines, input.display.groupingThresholdMeters);
}

/**
 * Regroupement par ancrage : la première ligne ouvre un groupe et en devient
 * l'ancre. Chaque ligne suivante rejoint le groupe si sa progression diffère de
 * celle de l'ancre de moins que le seuil **et** si aucun membre déjà présent
 * n'appartient au même POI qu'elle.
 *
 * Une ligne qui ne remplit pas ces deux conditions ferme le groupe courant et
 * ouvre le suivant, quelle que soit la condition en défaut : une ligne refusée
 * n'est jamais sautée, ce qui briserait la contiguïté de la liste.
 */
function groupAdjacentPois(lines: readonly OrderedLine[], thresholdMeters: number): readonly DisplayRow[] {
  const rows: DisplayRow[] = [];
  let members: PoiLineRef[] = [];
  let anchor: PoiLineRef | null = null;

  const flush = (): void => {
    if (anchor === null || members.length === 0) return;
    rows.push({
      kind: 'group',
      progressionMeters: anchor.progressionMeters,
      anchor,
      members,
    });
    members = [];
    anchor = null;
  };

  for (const line of lines) {
    if (line.poi === null) {
      flush();
      if (line.stop !== null) {
        rows.push({ kind: 'stop', progressionMeters: line.progressionMeters, stop: line.stop });
      } else if (line.range !== null) {
        rows.push({ kind: 'missingRange', progressionMeters: line.progressionMeters, range: line.range });
      }
      continue;
    }

    const candidate = line.poi;
    const currentAnchor = anchor;
    if (currentAnchor !== null) {
      const near = candidate.progressionMeters - currentAnchor.progressionMeters < thresholdMeters;
      const conflict = members.some((member) => member.poiId === candidate.poiId);
      if (near && !conflict) {
        members.push(candidate);
        continue;
      }
      flush();
    }

    anchor = candidate;
    members = [candidate];
  }
  flush();

  return rows;
}

/**
 * Première ligne dont la progression est supérieure ou égale à la valeur
 * saisie — commande « aller au kilomètre » de EF-RBK-010.
 *
 * Recherche dichotomique sur la liste, qui est triée par progression : aucun
 * parcours linéaire n'est admis, ENF-018 imposant la seconde sur 10 000 POI.
 */
export function findRowAtProgression(rows: readonly DisplayRow[], progressionMeters: number): number {
  let low = 0;
  let high = rows.length - 1;
  let found = rows.length > 0 ? rows.length - 1 : -1;
  while (low <= high) {
    const middle = (low + high) >> 1;
    const row = rows[middle];
    if (row === undefined) break;
    if (row.progressionMeters >= progressionMeters) {
      found = middle;
      high = middle - 1;
    } else {
      low = middle + 1;
    }
  }
  return found;
}

/**
 * Prochaine ligne de POI répondant à un prédicat, strictement après l'indice
 * donné. Retourne `-1` lorsque aucune cible n'existe en aval : la commande
 * reste alors visible mais désactivée, accompagnée de sa raison.
 */
export function findNextPoiRow(
  rows: readonly DisplayRow[],
  fromIndex: number,
  matches: (ref: PoiLineRef) => boolean,
): number {
  for (let i = Math.max(0, fromIndex + 1); i < rows.length; i += 1) {
    const row = rows[i];
    if (row === undefined || row.kind !== 'group') continue;
    if (row.members.some(matches)) return i;
  }
  return -1;
}

/** Nombre de POI distincts affichés : un POI à plusieurs passages compte une fois. */
export function countDisplayedPois(rows: readonly DisplayRow[]): number {
  const seen = new Set<PoiId>();
  for (const row of rows) {
    if (row.kind !== 'group') continue;
    for (const member of row.members) seen.add(member.poiId);
  }
  return seen.size;
}
